import { SITE, PAGES } from "./seo.config";

const LANGS = SITE.locales;

/** Split "/id/product/arcticstore/" -> { lang: "id", rest: "product/arcticstore" } */
export function parsePath(pathname = "/") {
  const clean = pathname.split(/[?#]/)[0].replace(/\/+$/, "");
  const parts = clean.split("/").filter(Boolean);
  const lang = LANGS.includes(parts[0]) ? parts[0] : SITE.defaultLocale;
  const rest = LANGS.includes(parts[0]) ? parts.slice(1).join("/") : parts.join("/");
  return { lang, rest };
}

function matchPage(rest) {
  for (const page of PAGES) {
    if (page.path === rest) return page;
    if (page.path.includes(":")) {
      const prefix = page.path.split("/:")[0];
      if (rest.startsWith(prefix + "/")) return page;
    }
  }
  // Unknown paths render the home page (see GuestRoutes), so use home metadata.
  return PAGES[0];
}

export const urlFor = (lang, path) =>
  `${SITE.url}/${lang}${path ? `/${path}` : ""}`;

function buildJsonLd(page, lang, canonical, title, description) {
  const org = SITE.organization;
  const ld = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: SITE.name,
      url: SITE.url,
      logo: SITE.logo,
      telephone: org.telephone,
      email: org.email,
      address: { "@type": "PostalAddress", ...org.address },
    },
  ];

  if (page.product) {
    ld.push({
      "@context": "https://schema.org",
      "@type": "Product",
      name: page.product.name,
      description,
      image: page.product.image,
      url: canonical,
      brand: { "@type": "Brand", name: "TITAN CONTAINERS" },
    });
  }

  if (page.path) {
    const crumbs = [
      { name: SITE.name, url: urlFor(lang, "") },
    ];
    const segs = page.path.split("/");
    segs.forEach((_, i) => {
      const sub = segs.slice(0, i + 1).join("/");
      const match = PAGES.find((p) => p.path === sub);
      if (match) {
        crumbs.push({
          name: (match.product?.name) || match.title[lang].split(" | ")[0].split(" - ")[0],
          url: urlFor(lang, sub),
        });
      }
    });
    if (crumbs.length > 1 && !page.path.includes(":")) {
      ld.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: c.name,
          item: c.url,
        })),
      });
    }
  }
  return ld;
}

/** Pure function: pathname -> every head value for that page. */
export function resolveSeo(pathname) {
  const { lang, rest } = parsePath(pathname);
  const page = matchPage(rest);
  const title = page.title[lang];
  const description = page.description[lang];
  const canonicalPath = page.path.includes(":") ? rest : page.path;
  const canonical = urlFor(lang, canonicalPath);

  const alternates = page.path.includes(":")
    ? []
    : [
        ...LANGS.map((l) => ({ hreflang: l, href: urlFor(l, page.path) })),
        { hreflang: "x-default", href: urlFor(SITE.defaultLocale, page.path) },
      ];

  return {
    lang,
    title,
    description,
    canonical,
    alternates,
    robots: page.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    og: {
      type: page.product ? "product" : "website",
      title,
      description,
      url: canonical,
      image: page.product?.image || SITE.ogImage,
      siteName: SITE.name,
      locale: SITE.ogLocale[lang],
      alternateLocale: SITE.ogLocale[LANGS.find((l) => l !== lang)],
    },
    jsonLd: page.noindex ? [] : buildJsonLd(page, lang, canonical, title, description),
  };
}

const esc = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/** Head markup used by the build-time prerender. Every tag carries data-seo
 *  so the client can replace (not duplicate) them on navigation. */
export function renderHeadTags(seo) {
  const t = [];
  t.push(`<title data-seo>${esc(seo.title)}</title>`);
  t.push(`<meta data-seo name="description" content="${esc(seo.description)}" />`);
  t.push(`<meta data-seo name="robots" content="${esc(seo.robots)}" />`);
  t.push(`<link data-seo rel="canonical" href="${esc(seo.canonical)}" />`);
  seo.alternates.forEach((a) =>
    t.push(`<link data-seo rel="alternate" hreflang="${a.hreflang}" href="${esc(a.href)}" />`)
  );
  const o = seo.og;
  [
    ["og:type", o.type],
    ["og:site_name", o.siteName],
    ["og:title", o.title],
    ["og:description", o.description],
    ["og:url", o.url],
    ["og:image", o.image],
    ["og:locale", o.locale],
    ["og:locale:alternate", o.alternateLocale],
    ["twitter:card", "summary_large_image"],
    ["twitter:title", o.title],
    ["twitter:description", o.description],
    ["twitter:image", o.image],
  ].forEach(([k, v]) => {
    const attr = k.startsWith("twitter") ? "name" : "property";
    t.push(`<meta data-seo ${attr}="${k}" content="${esc(v)}" />`);
  });
  seo.jsonLd.forEach((obj) =>
    t.push(
      `<script data-seo type="application/ld+json">${JSON.stringify(obj).replace(/</g, "\\u003c")}</script>`
    )
  );
  return t.join("\n  ");
}
